import { useState, useEffect } from 'react';
import { Tabs, Select, Button, Space, Tooltip, Alert } from 'antd';
import { FileExcelOutlined, CaretRightOutlined, WarningOutlined } from '@ant-design/icons';
import { APIClient } from '../../helpers/api_helper';
import Breadcrumb from '../../Common/Breadcrumb';
import ConfirmModal from '../../Common/ConfirmModal';
import WarnTriangle from '../../Common/WarnTriangle';
import { setInitialFilterConf } from '../../slices/filters/thunk'
import ProjectDetailsTab from './ProjectDetailsTab';
import ProjectBudgetExpensesPlan from './ProjectBudgetExpensesPlan';
import ProjectBudgetIncomesPlan from './ProjectBudgetIncomesPlan';
import ProjectIntentsTab from './ProjectIntentsTab';
import ProjectIncomeVsExpenseTab from './ProjectIncomeVsExpenseTab';
import useBudgetPlanData from './useBudgetPlanData';
import useProjectPresence from './useProjectPresence';
import { useDispatch } from 'react-redux';
import {
    useLocation,
    useNavigate,
    useParams
} from "react-router-dom";


const api = new APIClient();


const ProjectDetailsView = () => {

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(location.hash ? location.hash.substring(1) : "main");

  const { id } = useParams();
  const [projData, setProjData] = useState({});

  // Budget version state (lifted from ProjectBudgetExpensesPlan for tab bar display)
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [confirmVersionModal, setConfirmVersionModal] = useState(false);

  document.title = `Projekt #${id}` + process.env.REACT_APP_PAGE_TITLE;


  const loadProject = () => {
    if (id){
      api.get(`/api/projects/${id}/`).then((resp) => {
        setProjData(resp)
      });
    }
  }

  useEffect(() => {
    loadProject()
  }, []);

  useEffect(() => {
    dispatch(setInitialFilterConf({
      data: {},
      hidden: ['intent_type', 'intent_checkboxes_group', 'relateds_group', 'search', 'dt_range'],
      disabled: ['search']
    }))
  }, []);

  // Shared budget plan data hook - lifted here so both tabs share state
  const budgetPlanData = useBudgetPlanData({
    projData,
    loadProject,
    selectedVersion,
    setSelectedVersion,
    setHasDraft
  });

  const onTabSelect = key => {
    setActiveTab(key);
    navigate(`#${key}`, { replace: true });
  }

  const handleExportXlsx = async () => {
    setExporting(true);
    try {
      const versionParam = selectedVersion !== null && selectedVersion !== 'draft'
        ? `?version=${selectedVersion}`
        : '';
      const blob = await api.post(
        `/api/projects/${id}/export_budget_xlsx/${versionParam}`,
        {},
        { successMsg: null, responseType: 'blob' }
      );
      const versionSuffix = selectedVersion !== null && selectedVersion !== 'draft'
        ? `_v${selectedVersion}`
        : '_osnutek';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projData.title}_budget${versionSuffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  // Build version options for the select
  const versionOptions = [
    ...Array.from({ length: projData.version || 0 }, (_, i) => ({
      value: i + 1,
      label: `Verzija ${i + 1}`
    })),
    ...(hasDraft ? [{ value: 'draft', label: 'Osnutek' }] : []),
  ];

  const items = [
    {
      key: 'main',
      label: <span>Glavno</span>,
      children: <ProjectDetailsTab projData={projData} />,
    }
  ].concat(projData.id ? [
    {
      key: 'budget_expenses',
      label: <span>Odhodki</span>,
      children: <ProjectBudgetExpensesPlan
        budgetPlanData={budgetPlanData}
        selectedVersion={selectedVersion}
      />
    },
    {
      key: 'budget_incomes',
      label: <span>Prihodki</span>,
      children: <ProjectBudgetIncomesPlan
        projData={projData}
        budgetPlanData={budgetPlanData}
      />
    },
    {
      key: 'intents',
      label: <span>Transakcije</span>,
      children: <ProjectIntentsTab projData={projData} isActive={activeTab === 'intents'} onVersionCreated={() => { loadProject(); budgetPlanData.loadBudgetExpensesPlanData(null, true); }} />,
    },
    {
      key: 'income_vs_expense',
      label: <span>Prihodki vs odhodki</span>,
      children: <ProjectIncomeVsExpenseTab projData={projData} isActive={activeTab === 'income_vs_expense'} />,
    },
  ]: []);

  const {
    hasChanges,
    categoriesWithoutDate,
    onSubmit,
    handleSaveNewBudgetVersion,
    isSavingVersion,
    isSavingDraft,
    isLoadingVersion,
    nextVersion,
    isEditing,
    lastLoadedAt,
  } = budgetPlanData;

  const isOnBudgetTab = activeTab === 'budget_expenses' || activeTab === 'budget_incomes';
  const { presenceData, hasExternalSave, lastSavedBy } = useProjectPresence({
    projectId: projData.id,
    isActive: isOnBudgetTab,
    isEditing,
    lastLoadedAt,
  });

  const editingUsers = presenceData.filter(u => u.status === 'editing');

  const budgetExtraOps = (
    <Space>
      <span>Verzija načrta:</span>
      <Select
        value={selectedVersion}
        onChange={setSelectedVersion}
        options={versionOptions}
        style={{ width: 150 }}
        loading={isLoadingVersion}
        disabled={isLoadingVersion}
      />
      <Tooltip title={
        selectedVersion !== 'draft' ? "Najprej naredite spremembe in shranite osnutek, da lahko ustvarite novo verzijo" :
        hasChanges ? "Najprej shranite osnutek pred ustvarjanjem nove verzije" :
        categoriesWithoutDate.length > 0 ? `Manjkajoč datum namenov za kategorijo: ${categoriesWithoutDate.join(', ')}` : ''
      }>
        <Button
          onClick={() => setConfirmVersionModal(true)}
          disabled={selectedVersion !== 'draft' || hasChanges || categoriesWithoutDate.length > 0 || isSavingVersion}
          loading={isSavingVersion}
          type="primary"
          danger
        >
          <CaretRightOutlined /> Shrani v Direktor ver. #{nextVersion}
        </Button>
      </Tooltip>
      <Tooltip title={hasChanges ? "Shrani trenutne spremembe kot osnutek" : "Najprej naredite kakšno spremembo!"}>
        <Button type='primary' onClick={onSubmit} disabled={!hasChanges || isSavingDraft} loading={isSavingDraft}>Shrani (osnutek)</Button>
      </Tooltip>
      <Button onClick={handleExportXlsx} loading={exporting}>
        <FileExcelOutlined /> Prenesi ponudbo
      </Button>
    </Space>
  );

  const extraOps = (activeTab === 'budget_expenses' || activeTab === 'budget_incomes') ? { right: budgetExtraOps } : null;

  const projBreadStr = id ? `#${id} - ${projData?.title}` : ''
  return (
    <>
      <Breadcrumb parentTitle="Projekti" pageTitle={`Projekt ${projBreadStr}`} />
      <div style={{ position: 'sticky', top: 100, zIndex: 998 }}>
        {isOnBudgetTab && hasExternalSave ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 4 }}
            message={`${lastSavedBy} je shranil/-a spremembe na tem projektu.`}
            description={hasChanges
              ? 'Pozor: imate neshranjene spremembe. Če osvežite, boste izgubili lokalne spremembe.'
              : 'Osvežite stran, da dobite najnovejše podatke.'}
            action={
              <Button danger type="primary" onClick={() => budgetPlanData.loadBudgetExpensesPlanData(null, false)}>
                Osveži
              </Button>
            }
          />
        ) : isOnBudgetTab && !isEditing && editingUsers.length > 0 ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 4 }}
            message={`${editingUsers.map(u => u.name).join(', ')} trenutno ${editingUsers.length === 1 ? 'ureja' : 'urejajo'} proračun tega projekta!`}
            description="Sočasno urejanje lahko povzroči izgubo podatkov. Počakajte, da drugi uporabnik konča."
          />
        ) : isOnBudgetTab && presenceData.length > 0 ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 4 }}
            message={`Na proračunu tega projekta ${presenceData.length === 1 ? 'je' : 'so'} še: ${presenceData.map(u => u.name).join(', ')}`}
          />
        ) : null}
      </div>
      <Tabs activeKey={activeTab} onChange={onTabSelect} items={items} tabBarExtraContent={extraOps} />
      <ConfirmModal
        open={confirmVersionModal}
        onCancel={() => setConfirmVersionModal(false)}
        onOk={() => { handleSaveNewBudgetVersion(); setConfirmVersionModal(false); }}
        confirmBtnTitle="Shrani v direktor"
        confirmBtnProps={{ danger: true, icon: <WarningOutlined /> }}
        textComponent={<span>Ali ste prepričani, da želite shraniti verzijo {nextVersion}? <br/><WarnTriangle tooltip="Pozor" />To bo posodobilo oz. na novo ustvarilo obstoječe transakcije.</span>}
      />
    </>
  )
}

export default ProjectDetailsView;
